import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DeliveryOption, EnrichedSupplierForDelivery } from "../types";
import { calculateDiscountedPrice } from "./deliveryCostUtils";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST_TIMEZONE = "Asia/Kolkata";

export interface EstimateMaxBounds {
  /** Sum of discounted delivery costs across all non-deleted options. */
  deliveryCostMin: number;
  /** Latest cptMax among all non-deleted options (ISO UTC), if any. */
  latestOptionCptMaxIso: string | null;
}

function getActiveDeliveryOptions(
  suppliers: EnrichedSupplierForDelivery[],
): DeliveryOption[] {
  return suppliers
    .filter((supplier) => supplier.supplierId != null)
    .flatMap((supplier) =>
      (supplier.deliveryOptions ?? []).filter(
        (option) => !(option as { isDeleted?: boolean }).isDeleted,
      ),
    );
}

export function computeEstimateMaxBounds(
  suppliers: EnrichedSupplierForDelivery[],
): EstimateMaxBounds {
  const options = getActiveDeliveryOptions(suppliers);

  const deliveryCostMin = options.reduce((sum, option) => {
    const cost =
      option.deliveryCost == null ? 0 : Number(option.deliveryCost);
    return (
      sum +
      calculateDiscountedPrice(cost, option.deliveryCostDiscountPercentage)
    );
  }, 0);

  let latestMs: number | null = null;
  for (const option of options) {
    if (!option.cptMax) continue;
    const ms = dayjs(option.cptMax).valueOf();
    if (Number.isNaN(ms)) continue;
    if (latestMs == null || ms > latestMs) {
      latestMs = ms;
    }
  }

  return {
    deliveryCostMin,
    latestOptionCptMaxIso:
      latestMs == null ? null : dayjs(latestMs).toISOString(),
  };
}

export interface EstimateMaxBoundsValidation {
  isCptValid: boolean;
  isCostValid: boolean;
  /** True when empty or when filled values satisfy option bounds. */
  isValid: boolean;
  cptError: string | null;
  costError: string | null;
  bounds: EstimateMaxBounds;
}

/**
 * Validate ops-entered Max CPT / Max Delivery Cost against current options.
 * Empty values are allowed (fields are optional).
 * `maxCptLocal` is datetime-local IST (YYYY-MM-DDTHH:mm).
 */
export function validateEstimateMaxBounds(
  suppliers: EnrichedSupplierForDelivery[],
  maxCptLocal: string,
  maxDeliveryCost: number | undefined,
): EstimateMaxBoundsValidation {
  const bounds = computeEstimateMaxBounds(suppliers);

  let cptError: string | null = null;
  let isCptValid = true;
  if (maxCptLocal) {
    const maxCptUtc = dayjs.tz(maxCptLocal, IST_TIMEZONE).utc();
    if (!maxCptUtc.isValid()) {
      cptError = "Enter a valid Max CPT Time";
      isCptValid = false;
    } else if (
      bounds.latestOptionCptMaxIso &&
      maxCptUtc.valueOf() < dayjs(bounds.latestOptionCptMaxIso).valueOf()
    ) {
      cptError =
        "Max CPT Time must be greater than or equal to all delivery option CPT times";
      isCptValid = false;
    }
  }

  let costError: string | null = null;
  let isCostValid = true;
  if (maxDeliveryCost !== undefined && maxDeliveryCost !== null) {
    if (Number.isNaN(Number(maxDeliveryCost))) {
      costError = "Enter a valid Max Delivery Cost";
      isCostValid = false;
    } else if (Number(maxDeliveryCost) < bounds.deliveryCostMin) {
      costError = `Max Delivery Cost must be ≥ ₹${bounds.deliveryCostMin.toLocaleString(
        "en-IN",
      )} (sum of delivery option costs)`;
      isCostValid = false;
    }
  }

  return {
    isCptValid,
    isCostValid,
    isValid: isCptValid && isCostValid,
    cptError,
    costError,
    bounds,
  };
}

export function toEstimateCptMaxUtcIso(maxCptLocal: string): string {
  return dayjs.tz(maxCptLocal, IST_TIMEZONE).utc().toISOString();
}

export function toEstimateCptMaxLocal(isoUtc?: string | null): string {
  if (!isoUtc) return "";
  const local = dayjs(isoUtc).tz(IST_TIMEZONE);
  return local.isValid() ? local.format("YYYY-MM-DDTHH:mm") : "";
}
